import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  catalogRuntimeCapabilitiesQuery,
  productsQuery,
} from '@entities/product/api/product.queries'
import { ProductEditor, type ProductStepId } from '@features/product'
import { CheckoutBuilder } from '@features/checkout-builder'
import { ProductsList } from '@features/products-list'
import { RecuperaProductFeatureToggle } from '@features/recupera-ai'
import { useSession } from '@shared/lib/auth/session-store'
import { isSaleOperationBlocked } from '@shared/lib/auth/operation-eligibility'

export function ProdutosPage() {
  const user = useSession((state) => state.user)
  const canCreate = !isSaleOperationBlocked({
    isSaleBlocked: user?.isSaleBlocked,
    documentationStatus: user?.documentationStatus,
  })
  const [editorProductId, setEditorProductId] = useState<string | undefined>()
  const [editorInitialStep, setEditorInitialStep] = useState<ProductStepId | undefined>()
  const [editorOpen, setEditorOpen] = useState(false)
  const [newProductRequestId, setNewProductRequestId] = useState(0)
  const [checkoutBuilderTarget, setCheckoutBuilderTarget] = useState<
    { productId: string; requestId: number; checkoutId?: string } | undefined
  >()
  const checkoutBuilderRequestId = useRef(0)
  const productsResult = useQuery(productsQuery())
  const runtimeCapabilities = useQuery(catalogRuntimeCapabilitiesQuery())

  function openNewProduct() {
    setEditorProductId(undefined)
    setEditorInitialStep(undefined)
    setNewProductRequestId((current) => current + 1)
    setEditorOpen(true)
  }

  function openProduct(productId: string) {
    setEditorProductId(productId)
    setEditorInitialStep(undefined)
    setEditorOpen(true)
  }

  function openCheckoutBuilder(productId: string, checkoutId?: string) {
    checkoutBuilderRequestId.current += 1
    setCheckoutBuilderTarget({
      productId,
      requestId: checkoutBuilderRequestId.current,
      ...(checkoutId ? { checkoutId } : {}),
    })
  }

  return (
    <>
      <ProductsList
        products={productsResult.data ?? []}
        loading={productsResult.isPending}
        error={productsResult.isError}
        canCreate={canCreate}
        onCreate={openNewProduct}
        onOpen={openProduct}
        onRetry={() => void productsResult.refetch()}
      />
      <ProductEditor
        key={editorProductId ?? `new-${newProductRequestId}`}
        open={editorOpen}
        productId={editorProductId}
        initialStep={editorInitialStep}
        minuteBillingEnabled={runtimeCapabilities.data?.minuteBillingEnabled === true}
        onClose={() => setEditorOpen(false)}
        onProductCreated={(productId, initialStep) => {
          setEditorProductId(productId)
          setEditorInitialStep(initialStep)
          setEditorOpen(true)
        }}
        onOpenCheckoutBuilder={openCheckoutBuilder}
        featuresSlot={
          editorProductId
            ? (price) => <RecuperaProductFeatureToggle productId={editorProductId} price={price} />
            : undefined
        }
      />
      <CheckoutBuilder
        key={checkoutBuilderTarget?.requestId ?? 'closed'}
        open={Boolean(checkoutBuilderTarget)}
        productId={checkoutBuilderTarget?.productId}
        initialCheckoutId={checkoutBuilderTarget?.checkoutId}
        onClose={() => setCheckoutBuilderTarget(undefined)}
      />
    </>
  )
}
